import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export type LookbookFlipStyle = "curl" | "slide" | "fade";

export interface LookbookItem {
  id: string;
  file_path: string;
  media_type: "image" | "video";
  caption: string;
  sort_order: number;
  is_visible: boolean;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const PUBLIC_URL = `${SUPABASE_URL}/storage/v1/object/public/lookbook`;

export const lookbookFileUrl = (path: string) =>
  path.startsWith("http") ? path : `${PUBLIC_URL}/${path}`;

interface LookbookProps {
  items?: LookbookItem[];
  flipStyle?: LookbookFlipStyle;
  /** Compact preview used in admin settings */
  compact?: boolean;
  className?: string;
}

/**
 * Public lookbook viewer.
 *
 * If `items` is omitted, fetches visible items from the database.
 * If `flipStyle` is omitted, reads the `lookbook_flip_style` site setting.
 */
export const Lookbook = ({
  items: itemsProp,
  flipStyle: flipStyleProp,
  compact = false,
  className = "",
}: LookbookProps) => {
  const [items, setItems] = useState<LookbookItem[]>(itemsProp ?? []);
  const [flipStyle, setFlipStyle] = useState<LookbookFlipStyle>(
    flipStyleProp ?? "curl",
  );
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [loading, setLoading] = useState(!itemsProp);

  // Sync controlled props
  useEffect(() => {
    if (itemsProp) setItems(itemsProp);
  }, [itemsProp]);
  useEffect(() => {
    if (flipStyleProp) setFlipStyle(flipStyleProp);
  }, [flipStyleProp]);

  // Fetch items + style if uncontrolled
  useEffect(() => {
    if (itemsProp && flipStyleProp) return;
    let active = true;
    (async () => {
      const tasks: Promise<unknown>[] = [];
      if (!itemsProp) {
        tasks.push(
          (async () => {
            const { data } = await supabase
              .from("lookbook_items")
              .select("*")
              .eq("is_visible", true)
              .order("sort_order", { ascending: true })
              .order("created_at", { ascending: true });
            if (active && data) setItems(data as LookbookItem[]);
          })(),
        );
      }
      if (!flipStyleProp) {
        tasks.push(
          (async () => {
            const { data } = await supabase
              .from("site_settings")
              .select("value")
              .eq("key", "lookbook_flip_style")
              .maybeSingle();
            if (!active) return;
            const v = (data?.value ?? "curl") as LookbookFlipStyle;
            if (v === "curl" || v === "slide" || v === "fade") setFlipStyle(v);
          })(),
        );
      }
      await Promise.all(tasks);
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [itemsProp, flipStyleProp]);

  // Clamp page when items change
  useEffect(() => {
    if (page > items.length - 1) setPage(Math.max(0, items.length - 1));
  }, [items.length, page]);

  const totalPages = items.length;
  const current = items[page];

  const goNext = () => {
    if (totalPages === 0) return;
    setDirection(1);
    setPage((p) => (p + 1) % totalPages);
  };
  const goPrev = () => {
    if (totalPages === 0) return;
    setDirection(-1);
    setPage((p) => (p - 1 + totalPages) % totalPages);
  };

  // Keyboard navigation
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement)) return;
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  // Touch swipe
  const touchStartX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center ${
          compact ? "h-48" : "h-[420px]"
        } ${className}`}
      >
        <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground">
          Loading…
        </div>
      </div>
    );
  }

  if (totalPages === 0) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-3 border border-dashed border-border/50 rounded-md bg-card/40 ${
          compact ? "h-48" : "h-[420px]"
        } ${className}`}
      >
        <BookOpen size={28} className="text-muted-foreground/60" />
        <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">
          Lookbook is empty
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`relative outline-none ${className}`}
    >
      {/* Book stage */}
      <div
        className={`relative mx-auto ${
          compact ? "max-w-[360px] aspect-[3/4]" : "max-w-[520px] aspect-[3/4]"
        }`}
        style={{ perspective: "2000px" }}
      >
        {/* Static "back" page so curl reveals something underneath */}
        <div className="absolute inset-0 rounded-md bg-gradient-to-br from-card via-background to-card border border-border/40 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)]" />

        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <FlipPage
            key={current.id}
            item={current}
            direction={direction}
            style={flipStyle}
          />
        </AnimatePresence>

        {/* Page edge shadow */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-2 bg-gradient-to-r from-black/20 to-transparent rounded-l-md" />
      </div>

      {/* Caption */}
      {current.caption && (
        <p
          className={`text-center mt-4 font-serif italic text-foreground/80 ${
            compact ? "text-sm" : "text-base"
          }`}
        >
          {current.caption}
        </p>
      )}

      {/* Controls */}
      <div className="flex items-center justify-center gap-6 mt-4">
        <button
          type="button"
          onClick={goPrev}
          aria-label="Previous page"
          className="p-2 rounded-full border border-border/40 hover:border-primary/60 hover:bg-primary/5 transition-colors"
        >
          <ChevronLeft size={compact ? 14 : 18} />
        </button>
        <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
          {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          onClick={goNext}
          aria-label="Next page"
          className="p-2 rounded-full border border-border/40 hover:border-primary/60 hover:bg-primary/5 transition-colors"
        >
          <ChevronRight size={compact ? 14 : 18} />
        </button>
      </div>
    </div>
  );
};

interface FlipPageProps {
  item: LookbookItem;
  direction: 1 | -1;
  style: LookbookFlipStyle;
}

const FlipPage = ({ item, direction, style }: FlipPageProps) => {
  const variants = useMemo(() => {
    switch (style) {
      case "curl":
        return {
          initial: {
            rotateY: direction === 1 ? -160 : 160,
            x: direction === 1 ? "30%" : "-30%",
            opacity: 0,
            transformOrigin: direction === 1 ? "left center" : "right center",
            boxShadow: "0 0 0 rgba(0,0,0,0)",
          },
          animate: {
            rotateY: 0,
            x: "0%",
            opacity: 1,
            transformOrigin: direction === 1 ? "left center" : "right center",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.45)",
          },
          exit: {
            rotateY: direction === 1 ? 160 : -160,
            x: direction === 1 ? "-30%" : "30%",
            opacity: 0,
            transformOrigin: direction === 1 ? "right center" : "left center",
          },
          transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] as const },
        };
      case "slide":
        return {
          initial: {
            x: direction === 1 ? "100%" : "-100%",
            rotateY: direction === 1 ? -8 : 8,
            opacity: 0.4,
          },
          animate: { x: "0%", rotateY: 0, opacity: 1 },
          exit: {
            x: direction === 1 ? "-100%" : "100%",
            rotateY: direction === 1 ? 8 : -8,
            opacity: 0.4,
          },
          transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const },
        };
      case "fade":
      default:
        return {
          initial: { opacity: 0, y: 18, scale: 0.97 },
          animate: { opacity: 1, y: 0, scale: 1 },
          exit: { opacity: 0, y: -18, scale: 0.97 },
          transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
        };
    }
  }, [style, direction]);

  return (
    <motion.div
      className="absolute inset-0 rounded-md overflow-hidden bg-background border border-border/40"
      style={{ transformStyle: "preserve-3d", backfaceVisibility: "hidden" }}
      initial={variants.initial}
      animate={variants.animate}
      exit={variants.exit}
      transition={variants.transition}
    >
      {item.media_type === "video" ? (
        <video
          src={lookbookFileUrl(item.file_path)}
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        />
      ) : (
        <img
          src={lookbookFileUrl(item.file_path)}
          alt={item.caption || "Lookbook page"}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      )}
      {/* Subtle page sheen */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-black/0 via-white/[0.04] to-black/10" />
    </motion.div>
  );
};
